import {memo, useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';

import {Box, ButtonBase, InputBase, Stack, Typography} from '@mui/material';

import {Spinner} from '../../contentGenerator/components/ui';
import VarInput from '../../contentGenerator/components/VarInput';
import {resolveVars} from '../../contentGenerator/formulaUtils';
import SeoScoreBadge from '../../shared/seo/SeoScoreBadge';
import {FieldSeoPanel} from '../../shared/seo/SeoPanel';
import {CHECK_STATUS} from '../../shared/seo/seoConstants';
import {useDebouncedCallback} from '../../shared/useDebounce';
import {
    BUILT_IN_TOOLS,
    C,
    CMD_COLORS,
    STATUS_CYCLE,
    TYPE_CONFIG,
} from '../constants';
import {useReviewAI} from '../hooks/useReviewAI';
import {buildCommandInstruction, copyListingField} from '../utils';
import {StatusPill} from './shared';

const resolveCommandColor = (command) =>
    CMD_COLORS.find(
        (item) =>
            item.id === command?.colorId ||
            String(item.hex).toLowerCase() === String(command?.color).toLowerCase(),
    ) || {
        hex: command?.color || CMD_COLORS[0].hex,
        light: command?.color ? `${command.color}14` : CMD_COLORS[0].light,
    };

function FieldSearchTerms({terms, onChange}) {
    const [input, setInput] = useState('');

    const addTerms = (rawValue) => {
        const nextTerms = rawValue
            .split(/,|\n/)
            .map((term) => term.trim())
            .filter(Boolean);

        if (!nextTerms.length) return;
        onChange(Array.from(new Set([...(terms || []), ...nextTerms])));
    };

    const removeTerm = (termToRemove) => {
        onChange((terms || []).filter((term) => term !== termToRemove));
    };

    return (
        <Box sx={{borderTop: `1px solid ${C.green}22`, background: `${C.green}08`, padding: '8px 12px'}}>
            <Stack direction="row" alignItems="center" gap="6px" sx={{marginBottom: '6px'}}>
                <Typography component="span" sx={{fontSize: 10, fontWeight: 700, color: C.green}}>Search terms</Typography>
                {!!terms.length && (
                    <ButtonBase
                        onClick={() => onChange([])}
                        sx={{
                            border: 'none',
                            background: 'transparent',
                            color: C.textSoft,
                            fontFamily: 'inherit',
                            fontSize: 10,
                            cursor: 'pointer',
                            marginLeft: 'auto',
                        }}>
                        Clear
                    </ButtonBase>
                )}
            </Stack>
            <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px'}}>
                {(terms || []).map((term) => (
                    <Box
                        key={term}
                        component="span"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            background: C.greenL,
                            color: C.green,
                            border: `1px solid ${C.green}33`,
                            borderRadius: 999,
                            padding: '2px 8px',
                            fontSize: 11,
                            fontWeight: 500,
                        }}>
                        {term}
                        <ButtonBase
                            onClick={() => removeTerm(term)}
                            aria-label={`Remove term ${term}`}
                            sx={{
                                border: 'none',
                                background: 'transparent',
                                color: C.green,
                                cursor: 'pointer',
                                padding: 0,
                                lineHeight: 1,
                                fontSize: 13,
                            }}>
                            ×
                        </ButtonBase>
                    </Box>
                ))}
            </Box>
            <Stack direction="row" gap="6px">
                <InputBase
                    value={input}
                    onChange={(event) => setInput(event.target.value)}
                    onKeyDown={(event) => {
                        if ((event.key === 'Enter' || event.key === ',') && input.trim()) {
                            event.preventDefault();
                            addTerms(input);
                            setInput('');
                        }
                    }}
                    placeholder="Add keyword or phrase"
                    sx={{
                        flex: 1,
                        fontFamily: 'inherit',
                        fontSize: 12,
                        padding: '6px 9px',
                        border: `1px solid ${C.border2}`,
                        borderRadius: '8px',
                        background: C.surface,
                        color: C.ink,
                    }}
                />
                <ButtonBase
                    onClick={() => {
                        addTerms(input);
                        setInput('');
                    }}
                    sx={{
                        border: `1px solid ${C.green}33`,
                        background: C.greenL,
                        color: C.green,
                        borderRadius: '8px',
                        padding: '0 12px',
                        fontFamily: 'inherit',
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}>
                    Add
                </ButtonBase>
            </Stack>
        </Box>
    );
}

export default memo(function FieldCard({
    apiKey,
    batchCommandState = null,
    brandContext = '',
    chatOpen,
    charLimit = 0,
    customCommands = [],
    displayStatus = null,
    field,
    highlightSubstrings = [],
    inputImages = [],
    initialEditing = true,
    isGrouped = false,
    onDelete,
    onOpenChat,
    onRegenerate,
    onRunCommandAcrossSkus,
    onSelectVariant,
    onTabsScroll,
    publishIssues = [],
    onUpdate,
    profileSettings = null,
    selectedSkuCount = 0,
    seoAnalysis = null,
    selectedVariantKey = null,
    initialTabsScroll = null,
    variants = [],
    vars = [],
}) {
    const {rewriteTextPreservingVars} = useReviewAI({profileSettings, brandContext});
    const [editing, setEditing] = useState(initialEditing);
    const [localText, setLocalText] = useState(field.text);
    const [localQuestion, setLocalQuestion] = useState(field.question || '');
    const [loadingTool, setLoadingTool] = useState('');
    const [error, setError] = useState('');
    const variantTabsRef = useRef(null);
    const isFaq = field.type === 'faq';

    // Restore the variant tabs scroll position once on mount. The parent
    // (ListingDetail) keeps the last-known {left, top} in a ref keyed by
    // field.id and passes it as initialTabsScroll, so when the FieldCard
    // remounts on variant change the user doesn't lose their place in a
    // long subgroup list. If no saved scroll exists (e.g. after a listing
    // switch where field.id changed), fall back to scrolling the active
    // variant into view so the user can see where they are.
    useLayoutEffect(() => {
        if (!variantTabsRef.current) return;
        const saved = initialTabsScroll || {};
        if (saved.left || saved.top) {
            if (saved.left) variantTabsRef.current.scrollLeft = saved.left;
            if (saved.top) variantTabsRef.current.scrollTop = saved.top;
            return;
        }
        const activeTab = variantTabsRef.current.querySelector('[aria-pressed="true"]');
        if (activeTab && typeof activeTab.scrollIntoView === 'function') {
            activeTab.scrollIntoView({inline: 'nearest', block: 'nearest'});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [copied, setCopied] = useState(false);
    const [showTerms, setShowTerms] = useState(Boolean(field.searchTerms?.length));
    const [showSeo, setShowSeo] = useState(false);
    const cardRef = useRef(null);
    const typeConfig = TYPE_CONFIG[field.type] || TYPE_CONFIG.bullet;
    const visiblePublishIssues = publishIssues.slice(0, 3);
    const hasPublishIssues = visiblePublishIssues.length > 0;
    const workflowStatus = displayStatus || field.status || 'draft';
    const showBatchCommand =
        selectedSkuCount > 1 && Boolean(onRunCommandAcrossSkus) && !isGrouped;

    // Sync local text when field.text changes from outside (AI tools, undo, etc.)
    useEffect(() => {
        setLocalText(field.text);
    }, [field.text]);

    // Mirror the same sync for the FAQ question.
    useEffect(() => {
        setLocalQuestion(field.question || '');
    }, [field.question]);

    // Debounce parent updates while typing (300ms)
    const debouncedUpdate = useDebouncedCallback(
        (nextText) => {
            onUpdate(field.id, {
                text: nextText,
                status: field.status === 'approved' ? 'approved' : 'revised',
            });
        },
        300,
    );

    const debouncedQuestionUpdate = useDebouncedCallback(
        (nextQuestion) => {
            onUpdate(field.id, {
                question: nextQuestion,
                status: field.status === 'approved' ? 'approved' : 'revised',
            });
        },
        300,
    );

    const handleTextChange = useCallback((nextText) => {
        setLocalText(nextText);
        debouncedUpdate(nextText);
    }, [debouncedUpdate]);

    const handleQuestionChange = useCallback((nextQuestion) => {
        setLocalQuestion(nextQuestion);
        debouncedQuestionUpdate(nextQuestion);
    }, [debouncedQuestionUpdate]);

    const resolvedCharLimit = charLimit > 0 ? charLimit : field.maxChars || 0;
    const len = localText.length;
    const overLimit = resolvedCharLimit > 0 && len > resolvedCharLimit;
    const nearLimit =
        resolvedCharLimit > 0 && !overLimit && len >= resolvedCharLimit * 0.85;
    const hasTerms = (field.searchTerms || []).length > 0;
    const previewText = editing ? localText : field.text;
    const showResolvedPreview = vars.length > 0 && previewText.includes('{{');

    const commitLocalText = useCallback(
        (nextText = localText) => {
            const flushed = debouncedUpdate.flush?.() || false;

            if (!flushed && nextText !== field.text) {
                onUpdate(field.id, {
                    text: nextText,
                    status: field.status === 'approved' ? 'approved' : 'revised',
                });
            }
        },
        [debouncedUpdate, field.id, field.status, field.text, localText, onUpdate],
    );

    const exitEditMode = useCallback(() => {
        commitLocalText();
        debouncedQuestionUpdate.flush?.();
        setEditing(false);
    }, [commitLocalText, debouncedQuestionUpdate]);

    // Exit edit mode when clicking outside the card
    useEffect(() => {
        if (!editing) return;
        const handler = (event) => {
            if (cardRef.current && !cardRef.current.contains(event.target)) {
                exitEditMode();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [editing, exitEditMode]);

    const enterEdit = () => {
        setEditing(true);
    };

    const applyTextChange = (nextText) => {
        if (!nextText || nextText === localText) return;
        setLocalText(nextText);
        onUpdate(field.id, {
            text: nextText,
            status: 'revised',
            history: [...field.history, localText].filter(Boolean),
        });
    };

    const runTool = async (tool) => {
        if (!localText.trim()) {
            setError('Field is empty.');
            return;
        }

        setLoadingTool(tool.id);
        setError('');
        try {
            const nextText = await rewriteTextPreservingVars({
                apiKey,
                charLimit: resolvedCharLimit,
                fieldType: field.type,
                inputImages,
                originalText: localText,
                vars,
                instruction: buildCommandInstruction({
                    prompt: tool.prompt,
                    vars,
                }),
                maxTokens: 450,
            });
            applyTextChange(nextText);
        } catch (toolError) {
            setError(toolError.message);
        } finally {
            setLoadingTool('');
        }
    };

    const runToolAcrossSkus = (tool) => {
        if (!onRunCommandAcrossSkus || selectedSkuCount < 2) return;
        commitLocalText();

        // Let the flushed local edit reach the listing state before the
        // parent snapshots every matching field for confirmation.
        window.setTimeout(() => onRunCommandAcrossSkus(tool), 0);
    };

    const runEnrich = async () => {
        if (!localText.trim()) {
            setError('Field is empty.');
            return;
        }
        if (!hasTerms) {
            setShowTerms(true);
            setError('Add search terms first.');
            return;
        }

        setLoadingTool('enrich');
        setError('');
        try {
            const nextText = await rewriteTextPreservingVars({
                apiKey,
                charLimit: resolvedCharLimit,
                fieldType: field.type,
                inputImages,
                originalText: localText,
                vars,
                instruction: `Rewrite this text naturally incorporating these search terms where they fit organically: ${(field.searchTerms || [])
                    .map((term) => `"${term}"`)
                    .join(', ')}.
Keep the current tone and approximate length. Avoid keyword stuffing.
Return only the improved text.`,
                maxTokens: 500,
            });
            applyTextChange(nextText);
        } catch (toolError) {
            setError(toolError.message);
        } finally {
            setLoadingTool('');
        }
    };

    // Builds a targeted AI prompt from the field's currently-flagged SEO
    // checks and asks the model to rewrite only what the checks call out,
    // preserving everything else (voice, facts, variable tokens).
    const runSeoFix = async () => {
        if (!localText.trim()) {
            setError('Field is empty.');
            return;
        }
        const flagged = (seoAnalysis?.checks || []).filter(
            (c) => c.status === CHECK_STATUS.WARN || c.status === CHECK_STATUS.FAIL,
        );
        if (!flagged.length) return;

        setLoadingTool('seo-fix');
        setError('');
        try {
            const issueLines = flagged
                .map((c) => `- ${c.label}: ${c.message}`)
                .join('\n');

            const instruction = `Revise the following ${field.type} to fix the specific SEO issues listed below. Address every issue. Do not introduce facts that aren't in the original, and keep the same product, brand, and features.

SEO issues to fix:
${issueLines}

Constraints:
- Keep the original tone and meaning.
- Return only the improved ${field.type} text, nothing else.`;

            const nextText = await rewriteTextPreservingVars({
                apiKey,
                charLimit: resolvedCharLimit,
                fieldType: field.type,
                inputImages,
                originalText: localText,
                vars,
                instruction,
                maxTokens: 600,
            });
            applyTextChange(nextText);
        } catch (fixError) {
            setError(fixError.message);
        } finally {
            setLoadingTool('');
        }
    };

    const trimToLimit = async () => {
        if (!resolvedCharLimit || !localText.trim()) return;

        setLoadingTool('trim');
        setError('');
        try {
            const nextText = await rewriteTextPreservingVars({
                apiKey,
                charLimit: resolvedCharLimit,
                fieldType: field.type,
                inputImages,
                originalText: localText,
                vars,
                instruction: `Rewrite this text to fit within ${resolvedCharLimit} characters while preserving the strongest product details. Return only the rewritten text.`,
                maxTokens: 400,
            });
            applyTextChange(nextText);
        } catch (toolError) {
            setError(toolError.message);
        } finally {
            setLoadingTool('');
        }
    };

    const runRegenerate = async () => {
        if (!onRegenerate) return;

        setLoadingTool('regenerate');
        setError('');
        try {
            const nextText = await onRegenerate(field);
            applyTextChange(typeof nextText === 'string' ? nextText.trim() : '');
        } catch (regenerateError) {
            setError(regenerateError.message || 'Could not regenerate this field.');
        } finally {
            setLoadingTool('');
        }
    };

    // Shared button sx for the header action row
    const headerBtnSx = {
        border: `1px solid ${C.border}`,
        background: 'transparent',
        color: C.textSoft,
        borderRadius: '8px',
        padding: '3px 8px',
        fontSize: 10,
        cursor: 'pointer',
        fontFamily: 'inherit',
    };
    const editBtnSx = {
        ...headerBtnSx,
        border: `1px solid ${C.blue}33`,
        background: C.blueL,
        color: C.blue,
        fontWeight: 700,
        padding: '4px 10px',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,.55)',
    };

    return (
        <Box
            ref={cardRef}
            sx={{
                background: C.surface,
                border: `1px solid ${
                    editing
                        ? `${C.ink}33`
                        : chatOpen
                          ? `${C.purple}55`
                          : hasPublishIssues
                            ? `${C.amber}44`
                            : workflowStatus === 'approved'
                              ? `${C.teal}33`
                              : C.border
                }`,
                borderRadius: '12px',
                overflow: 'hidden',
                margin: editing || chatOpen ? '-1px' : 0,
                borderWidth: editing || chatOpen ? '2px' : '1px',
                boxShadow: editing || chatOpen
                    ? `0 4px 20px ${C.ink}12, 0 0 0 4px ${C.ink}06`
                    : 'none',
                transition: 'border-color .15s, box-shadow .15s, margin .15s',
                zIndex: editing || chatOpen ? 1 : 'auto',
                position: 'relative',
            }}>
            {/* ── Variant tabs ──
                Single-row horizontal scroll. The right-edge gradient fade
                signals there's more content to scroll to (replaces the
                hidden-scrollbar approach which had no affordance at all). */}
            {variants.length > 1 && (
                <Box
                    sx={{
                        position: 'relative',
                        borderBottom: `1px solid ${C.border}`,
                        background: C.surfaceAlt,
                    }}>
                <Box
                    ref={variantTabsRef}
                    onScroll={(event) => {
                        if (onTabsScroll) {
                            onTabsScroll({
                                left: event.currentTarget.scrollLeft,
                                top: event.currentTarget.scrollTop,
                            });
                        }
                    }}
                    sx={{
                        display: 'flex',
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': {display: 'none'},
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                    }}>
                    {variants.map((variant) => {
                        const isSelected = selectedVariantKey === variant.key;

                        return (
                            <ButtonBase
                                key={variant.key}
                                onClick={() => onSelectVariant(variant.key)}
                                aria-pressed={isSelected}
                                sx={{
                                    position: 'relative',
                                    border: 'none',
                                    background: isSelected ? C.surface : 'transparent',
                                    color: isSelected ? C.amber : C.textSoft,
                                    padding: '8px 14px',
                                    fontFamily: 'inherit',
                                    fontSize: 11,
                                    fontWeight: isSelected ? 700 : 600,
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0,
                                    transition: 'color .15s, background .15s',
                                    '&::after': isSelected ? {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        height: '2px',
                                        background: C.amber,
                                        borderRadius: '2px 2px 0 0',
                                    } : {},
                                }}>
                                {variant.label}
                            </ButtonBase>
                        );
                    })}
                </Box>
                    {/* Right-edge gradient fade — visual affordance that
                        more tabs exist beyond the visible area. Pointer
                        events disabled so it doesn't intercept clicks on
                        the tab underneath. */}
                    <Box
                        aria-hidden="true"
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: 28,
                            background: `linear-gradient(to right, ${C.surfaceAlt}00, ${C.surfaceAlt})`,
                            pointerEvents: 'none',
                        }}
                    />
                </Box>
            )}

            {/* ── Header ── */}
            <Stack
                direction="row"
                alignItems="center"
                gap="8px"
                sx={{
                    padding: '9px 12px',
                    borderBottom: `1px solid ${C.border}`,
                    background: editing ? C.surfaceAlt : chatOpen ? `${C.purple}08` : C.surfaceAlt,
                }}>
                <Box
                    component="span"
                    sx={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        background: typeConfig.color,
                        display: 'block',
                        flexShrink: 0,
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        fontSize: 10,
                        fontWeight: 700,
                        color: typeConfig.color,
                        textTransform: 'uppercase',
                        letterSpacing: '.07em',
                    }}>
                    {field.label}
                </Typography>
                <Typography
                    component="span"
                    sx={{
                        fontSize: 9,
                        color: C.textSoft,
                        background: C.surface,
                        borderRadius: 999,
                        padding: '2px 8px',
                        border: `1px solid ${C.border}`,
                    }}>
                    {field.type}
                </Typography>
                {seoAnalysis && (
                    <SeoScoreBadge
                        grade={seoAnalysis.grade}
                        score={seoAnalysis.score}
                        size="small"
                        onClick={() => setShowSeo((v) => !v)}
                    />
                )}
                <Box component="span" sx={{flex: 1}} />

                {/* Undo — visible in edit mode only */}
                {editing && !!field.history.length && (
                    <ButtonBase
                        onClick={() => {
                            const previousText = field.history[field.history.length - 1];
                            onUpdate(field.id, {
                                text: previousText,
                                history: field.history.slice(0, -1),
                                status: field.history.length > 1 ? 'revised' : 'draft',
                            });
                        }}
                        aria-label={`Undo last change (${field.history.length} in history)`}
                        sx={headerBtnSx}>
                        Undo {field.history.length}
                    </ButtonBase>
                )}

                <ButtonBase
                    onClick={async () => {
                        try {
                            await copyListingField(localText, vars);
                            setCopied(true);
                            setTimeout(() => setCopied(false), 1200);
                        } catch (copyError) {
                            setError(copyError.message);
                        }
                    }}
                    sx={{...headerBtnSx, color: copied ? C.ink : C.textSoft}}>
                    {copied ? 'Copied' : 'Copy'}
                </ButtonBase>

                {onRegenerate && (
                    <ButtonBase
                        onClick={runRegenerate}
                        disabled={Boolean(loadingTool)}
                        sx={{
                            ...headerBtnSx,
                            color: loadingTool === 'regenerate' ? C.ink : C.textSoft,
                            opacity: loadingTool && loadingTool !== 'regenerate' ? 0.45 : 1,
                        }}>
                        {loadingTool === 'regenerate' ? 'Regenerating' : 'Regenerate'}
                    </ButtonBase>
                )}

                {!editing && (
                    <ButtonBase onClick={enterEdit} sx={editBtnSx}>
                        Edit
                    </ButtonBase>
                )}

                {onOpenChat && (
                    <ButtonBase
                        onClick={onOpenChat}
                        sx={{
                            ...headerBtnSx,
                            border: `1px solid ${chatOpen ? `${C.purple}44` : C.border}`,
                            background: chatOpen ? C.purpleL : 'transparent',
                            color: chatOpen ? C.purple : C.textSoft,
                            fontSize: 11,
                            fontWeight: 600,
                            padding: '3px 9px',
                        }}>
                        {chatOpen ? 'Close chat' : 'Chat'}
                    </ButtonBase>
                )}

                {/* Status pill in view mode → Done button in edit mode */}
                {editing ? (
                    <ButtonBase
                        onClick={exitEditMode}
                        sx={{
                            border: `1px solid ${C.ink}22`,
                            background: C.ink,
                            color: C.surface,
                            borderRadius: '8px',
                            padding: '3px 10px',
                            fontSize: 10,
                            fontWeight: 700,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                        }}>
                        Done
                    </ButtonBase>
                ) : (
                    <StatusPill
                        status={workflowStatus}
                        onClick={() => {
                            const nextStatus = STATUS_CYCLE[workflowStatus] || 'draft';
                            onUpdate(field.id, {status: nextStatus});
                        }}
                    />
                )}

                <ButtonBase
                    onClick={onDelete}
                    aria-label={`Delete ${field.label}`}
                    sx={{
                        ...headerBtnSx,
                        padding: 0,
                        width: 26,
                        height: 26,
                        fontSize: 16,
                        lineHeight: 1,
                    }}>
                    ×
                </ButtonBase>
            </Stack>

            {/* ── Content area ── */}
            <Box
                sx={{padding: '12px'}}
                onDoubleClick={() => { if (!editing) enterEdit(); }}
                onKeyDown={(event) => {
                    if (event.key === 'Escape' && editing) exitEditMode();
                }}>
                {isFaq && (
                    <Box sx={{marginBottom: '8px'}}>
                        <Typography
                            component="span"
                            sx={{
                                display: 'block',
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.purple,
                                textTransform: 'uppercase',
                                letterSpacing: '.07em',
                                marginBottom: '4px',
                            }}>
                            Question
                        </Typography>
                        <VarInput
                            value={localQuestion}
                            onChange={handleQuestionChange}
                            vars={vars}
                            ariaLabel={`${field.label} question`}
                            placeholder="How do I know if this will fit my door?"
                            readOnly={!editing}
                            showPreview
                            style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                fontFamily: 'inherit',
                                fontSize: 14,
                                fontWeight: 600,
                                color: C.ink,
                                border: `1px solid ${C.border2}`,
                                borderRadius: '10px',
                                padding: '8px 12px',
                                background: C.surface,
                            }}
                        />
                        <Typography
                            component="span"
                            sx={{
                                display: 'block',
                                fontSize: 9,
                                fontWeight: 700,
                                color: C.purple,
                                textTransform: 'uppercase',
                                letterSpacing: '.07em',
                                marginTop: '10px',
                                marginBottom: '4px',
                            }}>
                            Answer
                        </Typography>
                    </Box>
                )}
                <VarInput
                    value={localText}
                    onChange={handleTextChange}
                    vars={vars}
                    multiline
                    rows={typeConfig.rows}
                    ariaLabel={`${field.label} ${isFaq ? 'answer' : 'content'}`}
                    highlightSubstrings={highlightSubstrings}
                    placeholder={
                        isFaq
                            ? 'Answer the question directly in the first sentence…'
                            : 'Type here… use {{ to insert a variable or formula'
                    }
                    readOnly={!editing}
                    showPreview={false}
                    style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        fontFamily: typeConfig.fontFamily,
                        fontSize: typeConfig.fontSize,
                        lineHeight: field.type === 'title' ? 1.35 : 1.65,
                        color: C.ink,
                        border: `1px solid ${overLimit ? `${C.coral}55` : nearLimit ? `${C.amber}55` : C.border2}`,
                        borderRadius: '10px',
                        padding: '10px 12px',
                        background: C.surface,
                    }}
                />
                {showResolvedPreview && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: C.ink,
                            marginTop: '6px',
                            padding: '8px 10px',
                            background: C.surfaceAlt,
                            border: `1px solid ${C.border}`,
                            borderRadius: '8px',
                            lineHeight: 1.5,
                            wordBreak: 'break-word',
                        }}>
                        <Typography
                            component="span"
                            sx={{fontSize: 10, fontWeight: 700, color: typeConfig.color, marginRight: '6px'}}>
                            {editing ? 'Preview' : 'Resolved'}
                        </Typography>
                        {resolveVars(previewText, vars)}
                    </Typography>
                )}
                {/* Char counter */}
                <Stack direction="row" alignItems="center" gap="8px" sx={{marginTop: '6px'}}>
                    <Stack direction="row" alignItems="center" gap="8px" sx={{flex: 1}}>
                        {resolvedCharLimit > 0 ? (
                            <>
                                <Box
                                    sx={{
                                        flex: 1,
                                        height: 4,
                                        background: C.surfaceAlt,
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                    }}>
                                    <Box
                                        sx={{
                                            width: `${Math.min((len / resolvedCharLimit) * 100, 100)}%`,
                                            height: '100%',
                                            background: overLimit ? C.coral : nearLimit ? C.amber : C.teal,
                                        }}
                                    />
                                </Box>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: overLimit
                                            ? C.coral
                                            : nearLimit
                                              ? C.amber
                                              : C.textSoft,
                                    }}>
                                    {len}/{resolvedCharLimit}
                                </Typography>
                                {editing && overLimit && (
                                    <ButtonBase
                                        onClick={trimToLimit}
                                        disabled={loadingTool === 'trim'}
                                        sx={{
                                            border: `1px solid ${C.coral}33`,
                                            background: C.coralL,
                                            color: C.coral,
                                            borderRadius: '8px',
                                            padding: '3px 8px',
                                            fontSize: 10,
                                            fontWeight: 700,
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                        }}>
                                        {loadingTool === 'trim' ? 'Trimming' : 'Trim'}
                                    </ButtonBase>
                                )}
                            </>
                        ) : (
                            <Typography component="span" sx={{fontSize: 10, color: C.textSoft}}>
                                {len} chars
                            </Typography>
                        )}
                    </Stack>
                    {error && (
                        <Typography component="span" role="alert" sx={{fontSize: 11, color: C.coral}}>
                            {error}
                        </Typography>
                    )}
                </Stack>
                {hasPublishIssues && (
                    <Box
                        sx={{
                            marginTop: '8px',
                            padding: '10px 12px',
                            borderRadius: '10px',
                            border: `1px solid ${C.amber}22`,
                            background: `${C.amber}0d`,
                        }}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap="6px"
                            sx={{marginBottom: '8px'}}>
                            <Box
                                component="span"
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    background: C.amber,
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 800,
                                    color: C.amber,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.07em',
                                }}>
                                Publish check
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    color: C.textFaint,
                                    fontWeight: 600,
                                    marginLeft: 'auto',
                                }}>
                                {publishIssues.filter((i) => !i.isIgnored).length}/
                                {publishIssues.length}
                                {' '}active
                            </Typography>
                        </Stack>
                        <Stack direction="column" gap="4px">
                            {visiblePublishIssues.map((issue) => {
                                const ignored = Boolean(issue.isIgnored);
                                const toggleIgnore = () => {
                                    const current = Array.isArray(field.ignoredIssueIds)
                                        ? field.ignoredIssueIds
                                        : [];
                                    const next = ignored
                                        ? current.filter((id) => id !== issue.id)
                                        : Array.from(new Set([...current, issue.id]));
                                    onUpdate(field.id, {ignoredIssueIds: next});
                                };
                                return (
                                    <Stack
                                        key={issue.id}
                                        direction="row"
                                        alignItems="center"
                                        gap="10px"
                                        sx={{
                                            padding: '6px 8px',
                                            borderRadius: '8px',
                                            background: ignored
                                                ? `${C.textFaint}10`
                                                : C.surface,
                                            border: `1px solid ${
                                                ignored
                                                    ? `${C.textFaint}22`
                                                    : `${C.amber}1f`
                                            }`,
                                        }}>
                                        <Box
                                            component="span"
                                            sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: '50%',
                                                background: ignored
                                                    ? `${C.textFaint}22`
                                                    : `${C.amber}22`,
                                                color: ignored
                                                    ? C.textFaint
                                                    : C.amber,
                                                fontSize: 9,
                                                fontWeight: 800,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}>
                                            {ignored ? '✓' : '!'}
                                        </Box>
                                        <Typography
                                            sx={{
                                                flex: 1,
                                                minWidth: 0,
                                                fontSize: 11.5,
                                                color: ignored
                                                    ? C.textFaint
                                                    : C.ink,
                                                lineHeight: 1.45,
                                                textDecoration: ignored
                                                    ? 'line-through'
                                                    : 'none',
                                                fontStyle: ignored
                                                    ? 'italic'
                                                    : 'normal',
                                            }}>
                                            {issue.message}
                                        </Typography>
                                        <ButtonBase
                                            onClick={toggleIgnore}
                                            title={
                                                ignored
                                                    ? 'Restore this warning — it will block publish again'
                                                    : 'Ignore this warning — it will no longer block publish'
                                            }
                                            sx={{
                                                flexShrink: 0,
                                                border: `1px solid ${
                                                    ignored
                                                        ? `${C.teal}55`
                                                        : `${C.amber}55`
                                                }`,
                                                background: ignored
                                                    ? C.tealL
                                                    : C.surface,
                                                color: ignored ? C.teal : C.amber,
                                                borderRadius: '6px',
                                                padding: '3px 9px',
                                                fontFamily: 'inherit',
                                                fontSize: 10,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                lineHeight: 1.4,
                                                textTransform: 'uppercase',
                                                letterSpacing: '.04em',
                                                minWidth: 58,
                                                textAlign: 'center',
                                            }}>
                                            {ignored ? 'Restore' : 'Ignore'}
                                        </ButtonBase>
                                    </Stack>
                                );
                            })}
                            {publishIssues.length > visiblePublishIssues.length && (
                                <Typography
                                    sx={{
                                        fontSize: 10.5,
                                        color: C.textFaint,
                                        lineHeight: 1.4,
                                        paddingLeft: '6px',
                                        marginTop: '2px',
                                    }}>
                                    +{publishIssues.length - visiblePublishIssues.length} more warning
                                    {publishIssues.length - visiblePublishIssues.length === 1
                                        ? ''
                                        : 's'}
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                )}
            </Box>

            {/* ── Tools row — edit mode only ── */}
            {editing && (
                <Box sx={{padding: '0 12px 12px'}}>
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: '6px'}}>
                        {[...BUILT_IN_TOOLS, ...customCommands].map((command) => {
                            const color = resolveCommandColor(command);
                            const busy = loadingTool === command.id;
                            const batchBusy =
                                batchCommandState?.commandId === command.id;
                            return (
                                <Box
                                    key={command.id}
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        border: `1px solid ${color.hex}33`,
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                        background: `${color.hex}08`,
                                        boxShadow:
                                            'inset 0 1px 0 rgba(255,255,255,.65), 0 1px 2px rgba(22,35,48,.04)',
                                        transition:
                                            'background .15s, border-color .15s, box-shadow .15s',
                                        '&:hover': {
                                            background: color.light,
                                            borderColor: `${color.hex}55`,
                                            boxShadow:
                                                'inset 0 1px 0 rgba(255,255,255,.7), 0 2px 5px rgba(22,35,48,.08)',
                                        },
                                    }}>
                                    <ButtonBase
                                        onClick={() => runTool(command)}
                                        disabled={Boolean(
                                            loadingTool || batchCommandState,
                                        )}
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '5px',
                                            border: 'none',
                                            background: busy ? color.light : 'transparent',
                                            color: color.hex,
                                            borderRadius:
                                                showBatchCommand
                                                    ? '999px 0 0 999px'
                                                    : 999,
                                            padding: '5px 11px',
                                            minHeight: 27,
                                            fontSize: 11.5,
                                            fontWeight: 700,
                                            letterSpacing: '.005em',
                                            cursor: 'pointer',
                                            fontFamily: 'inherit',
                                            transition: 'background .15s, opacity .15s',
                                            '&:hover': {
                                                background: `${color.hex}0C`,
                                            },
                                            '&.Mui-focusVisible': {
                                                boxShadow: `inset 0 0 0 2px ${color.hex}55`,
                                            },
                                            opacity:
                                                (loadingTool && !busy) || batchCommandState
                                                    ? 0.45
                                                    : 1,
                                        }}>
                                        {busy && <Spinner dark size={11} />}
                                        {command.label}
                                    </ButtonBase>
                                    {showBatchCommand && (
                                        <ButtonBase
                                            onClick={() => runToolAcrossSkus(command)}
                                            disabled={Boolean(
                                                loadingTool || batchCommandState,
                                            )}
                                            aria-label={`Apply ${command.label} to all selected SKUs`}
                                            title="Apply to all selected SKUs"
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: 'none',
                                                borderLeft: `1px solid ${color.hex}33`,
                                                background: batchBusy
                                                    ? color.light
                                                    : 'transparent',
                                                color: color.hex,
                                                borderRadius: '0 999px 999px 0',
                                                padding: 0,
                                                width: 27,
                                                minHeight: 27,
                                                alignSelf: 'stretch',
                                                fontSize: 11,
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                fontFamily: 'inherit',
                                                transition:
                                                    'background .15s, opacity .15s',
                                                '&:hover': {
                                                    background: `${color.hex}14`,
                                                },
                                                '&.Mui-focusVisible': {
                                                    boxShadow: `inset 0 0 0 2px ${color.hex}55`,
                                                },
                                                opacity:
                                                    batchCommandState && !batchBusy
                                                        ? 0.45
                                                        : 1,
                                            }}>
                                            {batchBusy && <Spinner dark size={10} />}
                                            {!batchBusy && (
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'block',
                                                        lineHeight: 1,
                                                    }}>
                                                    ↗
                                                </Box>
                                            )}
                                        </ButtonBase>
                                    )}
                                </Box>
                            );
                        })}
                        <ButtonBase
                            onClick={runEnrich}
                            disabled={Boolean(batchCommandState || loadingTool === 'enrich')}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '5px',
                                border: `1px solid ${C.green}33`,
                                background: hasTerms ? C.greenL : 'transparent',
                                color: C.green,
                                borderRadius: 999,
                                padding: '4px 10px',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                            }}>
                            {loadingTool === 'enrich' && <Spinner dark size={11} />}
                            Enrich{hasTerms ? ` (${field.searchTerms.length})` : ''}
                        </ButtonBase>
                        <ButtonBase
                            onClick={() => setShowTerms((current) => !current)}
                            sx={{
                                border: `1px solid ${hasTerms ? `${C.green}33` : C.border}`,
                                background: showTerms ? `${C.green}10` : 'transparent',
                                color: hasTerms ? C.green : C.textSoft,
                                borderRadius: 999,
                                padding: '4px 10px',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                                fontFamily: 'inherit',
                                marginLeft: 'auto',
                            }}>
                            Terms{hasTerms ? ` (${field.searchTerms.length})` : ''}
                        </ButtonBase>
                    </Box>
                </Box>
            )}

            {/* Search terms — only while editing */}
            {editing && showTerms && (
                <FieldSearchTerms
                    terms={field.searchTerms || []}
                    onChange={(searchTerms) => onUpdate(field.id, {searchTerms})}
                />
            )}

            {/* SEO detail panel — toggled by clicking the SEO badge */}
            {showSeo && seoAnalysis && (
                <FieldSeoPanel
                    analysis={seoAnalysis}
                    onFix={runSeoFix}
                    fixing={loadingTool === 'seo-fix'}
                />
            )}
        </Box>
    );
})
